import { colyseusClient } from '../lobby/colyseus.js';
import {
  fetchRooms,
  fetchLobbiesStats,
  fetchMyActiveRooms,
  fetchWatchableRooms,
  fetchDailyBoard,
  fetchFactions,
  type PublicRoomSummary,
  type LobbiesStats,
  type MyActiveRoom,
  type WatchableRoom,
  type DailyBoard,
} from '../lobby/api.js';
import type { Router } from '../router.js';
import { el } from './login.js';
import { auth } from '../auth/state.js';
import { rankFromPoints } from '../game/rank.js';
import { startRankedMatchmaking } from './ranked_matchmaking.js';
import { ensureMusicPlaying } from '../sound/music.js';
import { markTutorialActive, clearTutorial } from './tutorial.js';

/**
 * Promise.race a value-returning promise against a timeout. Used to bound the
 * Colyseus create/join calls so the UI doesn't appear hung indefinitely when
 * the multiplayer server is unreachable (e.g. port conflict, dev restart in
 * progress, network drop).
 */
function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (err) => {
        clearTimeout(t);
        reject(err);
      },
    );
  });
}
const COLYSEUS_TIMEOUT_MS = 8000;

/**
 * Lobby browser: list of public rooms + buttons to create or join.
 * Polls /api/rooms every 5 seconds while mounted.
 */
let pollHandle: number | null = null;
let statsPollHandle: number | null = null;
let minePollHandle: number | null = null;
let watchPollHandle: number | null = null;

export function unmountLobbyBrowser(): void {
  if (pollHandle !== null) {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
  if (statsPollHandle !== null) {
    window.clearInterval(statsPollHandle);
    statsPollHandle = null;
  }
  if (minePollHandle !== null) {
    window.clearInterval(minePollHandle);
    minePollHandle = null;
  }
  if (watchPollHandle !== null) {
    window.clearInterval(watchPollHandle);
    watchPollHandle = null;
  }
}

export function renderLobbyBrowser(container: HTMLElement, router: Router): void {
  unmountLobbyBrowser();
  container.innerHTML = '';
  container.classList.add('overlay-screen');

  const wrap = el('div', 'lobby-wrap');

  // ── Resume banner ─────────────────────────────────────────────────
  // Empty container at the very top of the hub. If the player closed
  // their tab mid-game (or got disconnected) and the room is still
  // running, a pulsing "Revenir à ma chasse" button appears here so
  // they can hop straight back in without browsing the salon list.
  // Populated by refreshMine() below — invisible when there's nothing
  // to come back to.
  const resumeHost = el('div', 'home-resume-host');
  wrap.appendChild(resumeHost);

  // ── Hub header: profile widget on the left, big action buttons
  // on the right. Lives at the top of the home page so the player
  // sees who they are AND what they can do at a glance, without
  // having to navigate to /profile first.
  const hub = el('div', 'home-hub');
  hub.appendChild(renderProfileWidget(router));
  hub.appendChild(renderHomeActions(router));
  wrap.appendChild(hub);

  // ── Salon list section ────────────────────────────────────────────
  const listHeader = el('div', 'home-section-header');
  const listTitle = document.createElement('h2');
  listTitle.className = 'home-section-title';
  const listIcon = document.createElement('img');
  listIcon.src = '/sprites/iconchat_crier.png';
  listIcon.className = 'page-title-icon';
  listIcon.alt = '';
  listTitle.appendChild(listIcon);
  listTitle.appendChild(document.createTextNode('Jardins ouverts'));
  listHeader.appendChild(listTitle);
  // Live pill — "X salons · Y chatons" — filled in by applyLiveStats.
  const publicStat = el('div', 'home-public-stat');
  const psIcon = document.createElement('img');
  psIcon.src = '/sprites/iconchat_crier.png';
  psIcon.className = 'home-public-stat-icon';
  psIcon.alt = '';
  publicStat.appendChild(psIcon);
  publicStat.appendChild(el('span', 'home-public-stat-value', '—'));
  listHeader.appendChild(publicStat);
  wrap.appendChild(listHeader);

  const listWrap = el('div', 'lobby-list');
  wrap.appendChild(listWrap);

  // ── Chasses en cours (spectator) ──────────────────────────────────
  // Games that already started. Anyone can hop in as a read-only
  // spectator — handy to watch a friend's ranked match or learn from
  // stronger players.
  const watchHeader = el('div', 'home-section-header');
  const watchTitle = document.createElement('h2');
  watchTitle.className = 'home-section-title';
  const watchIcon = document.createElement('img');
  watchIcon.src = '/sprites/iconchat_neutre.png';
  watchIcon.className = 'page-title-icon';
  watchIcon.alt = '';
  watchTitle.appendChild(watchIcon);
  watchTitle.appendChild(document.createTextNode('Chasses en cours'));
  watchHeader.appendChild(watchTitle);
  wrap.appendChild(watchHeader);
  const watchWrap = el('div', 'lobby-list home-watch-list');
  wrap.appendChild(watchWrap);
  container.appendChild(wrap);

  let lastWatch: WatchableRoom[] = [];
  const refreshWatch = async (): Promise<void> => {
    try {
      const rooms = await fetchWatchableRooms();
      if (
        rooms.length === lastWatch.length &&
        rooms.every((r, i) => r.roomId === lastWatch[i]?.roomId && r.connected === lastWatch[i]?.connected)
      ) return;
      lastWatch = rooms;
      renderWatchList(watchWrap, rooms, router);
    } catch {
      // keep the previous list
    }
  };
  void refreshWatch();
  watchPollHandle = window.setInterval(refreshWatch, 5000);

  let lastRooms: PublicRoomSummary[] = [];

  const refresh = async (): Promise<void> => {
    try {
      const rooms = await fetchRooms();
      // Avoid flicker if nothing changed
      if (
        rooms.length === lastRooms.length &&
        rooms.every((r, i) => r.roomId === lastRooms[i]?.roomId && r.players === lastRooms[i]?.players)
      ) {
        return;
      }
      lastRooms = rooms;
      renderRoomList(listWrap, rooms, router);
    } catch {
      listWrap.innerHTML = '';
      listWrap.appendChild(
        el('p', 'lobby-empty', 'Impossible de récupérer la liste des salons.'),
      );
    }
  };

  void refresh();
  pollHandle = window.setInterval(refresh, 5000);

  // Also poll the live ranked counters every 5 s. We piggy-back on
  // the same interval handle list (cleared on unmount).
  const refreshStats = async (): Promise<void> => {
    try {
      const stats = await fetchLobbiesStats();
      applyLiveStats(stats);
    } catch {
      // Silently keep the last known values; counters just stop updating.
    }
  };
  void refreshStats();
  statsPollHandle = window.setInterval(refreshStats, 5000);

  // ── Resume button poll ───────────────────────────────────────────
  // Check every 5 s whether the user has any room they're still a
  // participant of. Cheap (one matchmaker.query call), and only the
  // logged-in user's id is matched. We swallow errors silently — if
  // the endpoint is down, we just don't show the button this tick.
  const refreshMine = async (): Promise<void> => {
    try {
      const mine = await fetchMyActiveRooms();
      renderResumeBanner(resumeHost, mine, router);
    } catch {
      // keep whatever we had — better than flashing it off.
    }
  };
  void refreshMine();
  minePollHandle = window.setInterval(refreshMine, 5000);
}

/**
 * Pop a pulsing "Revenir à ma chasse" pill at the top of the hub for
 * each room the user is still a participant of. Most of the time
 * there's at most one (you only play one game at a time), but the
 * server can return multiple if the user is wedged in two — we render
 * them all in case.
 *
 * Idempotent — re-runs on every poll. Skipping the redraw when the
 * room set is unchanged would be nicer but the cost of a quick
 * innerHTML wipe is negligible for the at-most-handful of rooms.
 */
function renderResumeBanner(host: HTMLElement, rooms: MyActiveRoom[], router: Router): void {
  host.innerHTML = '';
  if (rooms.length === 0) return;
  for (const room of rooms) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'home-resume-btn';
    if (room.ranked) btn.classList.add('home-resume-btn-ranked');

    const ico = document.createElement('img');
    ico.src = '/sprites/iconchat_guerrier.png';
    ico.className = 'home-resume-icon';
    ico.alt = '';
    btn.appendChild(ico);

    const text = el('span', 'home-resume-text');
    text.appendChild(el('span', 'home-resume-title', '⚔ Revenir à ma chasse'));
    const sub = el(
      'span',
      'home-resume-sub',
      `${room.name} · ${room.players}/${room.maxPlayers} chatons${room.ranked ? ' · classé' : ''}`,
    );
    text.appendChild(sub);
    btn.appendChild(text);

    const chev = el('span', 'home-resume-chev', '›');
    btn.appendChild(chev);

    btn.addEventListener('click', () => void resumeRoom(router, room));
    host.appendChild(btn);
  }
}

/**
 * Re-enter an active room. The server's `onJoin` reconnection path
 * recognises the user by their session userId and re-links them to
 * their existing Participant entry, so we don't need any special
 * "reconnect" call — `joinById` is enough.
 */
/**
 * Spin up a one-shot tutorial game:
 *   1. Create a private lobby (so it doesn't show in the public list).
 *   2. Pick the player's favourite faction (or a random one).
 *   3. Add a single easy-AI opponent on slot 1 with a different faction.
 *   4. Mark the player ready + send `game.start`.
 *   5. Stash the tutorial flag so the in-game mount knows to attach
 *      the overlay (talking head + chipmunk voice + step engine).
 *
 * Everything is async on the network round-trips, so we surface a
 * flash banner on error and bail without navigating.
 */
async function startTutorial(router: Router): Promise<void> {
  ensureMusicPlaying();
  if (!auth.user) {
    flashBanner('Connecte-toi d\'abord pour suivre le tuto.');
    return;
  }
  let factions: Awaited<ReturnType<typeof fetchFactions>>;
  try {
    factions = await fetchFactions();
  } catch {
    flashBanner('Impossible de charger les royaumes — réessaye.');
    return;
  }
  if (factions.length < 2) {
    flashBanner('Pas assez de royaumes pour lancer un tuto.');
    return;
  }
  // Pick faction for the player: favourite if any, otherwise random.
  const favoriteId = auth.user.favoriteFactionId ?? '';
  const playerFaction =
    factions.find((f) => f.id === favoriteId)?.id
    ?? factions[Math.floor(Math.random() * factions.length)]!.id;
  // Pick a different faction for the AI opponent.
  const otherFactions = factions.filter((f) => f.id !== playerFaction);
  const botFaction = otherFactions[Math.floor(Math.random() * otherFactions.length)]!.id;

  let room: Awaited<ReturnType<typeof colyseusClient.create>>;
  try {
    room = await withTimeout(
      colyseusClient.create('lobby', {
        name: 'Tuto chaton',
        isPrivate: true,
        // Server stashes this in metadata so the resume-list filter
        // excludes finished / abandoned tutorial rooms.
        tutorial: true,
      }),
      COLYSEUS_TIMEOUT_MS,
      'Le serveur multijoueur ne répond pas. Vérifie qu\'il tourne (npm run dev).',
    );
  } catch (err) {
    flashBanner((err as Error)?.message ?? 'Impossible d\'ouvrir le tuto.');
    return;
  }
  // Stash the room handle for the room screen to consume.
  (window as unknown as { __wolRoom?: typeof room }).__wolRoom = room;

  // Mark the session as a tutorial — the in-game mount reads this
  // and activates the overlay framework + step engine.
  markTutorialActive({
    playerFactionId: playerFaction,
    botFactionId: botFaction,
  });
  // When the player ACTUALLY leaves this room (clicks quit, server
  // closes the room, page is refreshed), drop the tutorial flag so
  // the next /room/* visit doesn't accidentally re-attach the
  // overlay on a normal match.
  room.onLeave(() => clearTutorial());

  // Fire the setup messages back-to-back. The server applies them in
  // order, and the `game.start` won't pass until every slot is ready
  // + has a faction. Small await between calls is unnecessary — the
  // single WebSocket guarantees ordering.
  try {
    room.send('faction.set', { factionId: playerFaction });
    room.send('ready.toggle');
    // Slot 1 becomes an easy AI.
    room.send('slot.set', { index: 1, type: 'ai_easy' });
    room.send('slot.faction.set', { index: 1, factionId: botFaction });
    // Close every other slot so tryStartGame doesn't wait for them.
    for (let i = 2; i < 8; i++) {
      room.send('slot.set', { index: i, type: 'closed' });
    }
    // Tight map: the server clamps continent count to a minimum of 3,
    // so we ask for the smallest legal value. Combined with only 2
    // players, this keeps the tutorial opponents close enough on the
    // map that every "now attack the enemy" step has an obvious target.
    room.send('map.setContinents', { n: 3 });
    room.send('game.start');
  } catch (err) {
    flashBanner((err as Error)?.message ?? 'Le tuto n\'a pas pu démarrer.');
    return;
  }
  router.navigate(`/room/${room.roomId}`);
}

async function resumeRoom(router: Router, summary: MyActiveRoom): Promise<void> {
  // The user gesture (their click on the resume button) is still live
  // here — fire the music kick now so the AudioContext is allowed to
  // resume per browser autoplay rules. If we wait until `mountInGame`
  // runs (after the async joinById round-trip), the gesture window
  // may have closed and `audioCtx.resume()` would silently no-op.
  ensureMusicPlaying();
  try {
    const room = await withTimeout(
      colyseusClient.joinById(summary.roomId, {}),
      COLYSEUS_TIMEOUT_MS,
      'Le serveur multijoueur ne répond pas.',
    );
    (window as unknown as { __wolRoom?: typeof room }).__wolRoom = room;
    router.navigate(`/room/${room.roomId}`);
  } catch (err) {
    flashBanner((err as Error)?.message ?? 'Impossible de revenir dans la partie.');
  }
}

/** Pin the latest stats onto the DOM elements the home hub already
 *  mounted. Idempotent — re-running on stale stats is harmless. */
function applyLiveStats(stats: LobbiesStats): void {
  const playing = document.querySelector<HTMLElement>('.home-ranked-stat-playing-value');
  const searching = document.querySelector<HTMLElement>('.home-ranked-stat-searching-value');
  const publicCount = document.querySelector<HTMLElement>('.home-public-stat-value');
  if (playing) playing.textContent = stats.rankedPlaying.toString();
  if (searching) searching.textContent = stats.rankedSearching.toString();
  if (publicCount) {
    publicCount.textContent = `${stats.publicLobbies} salon${stats.publicLobbies > 1 ? 's' : ''} · ${stats.publicPlayers} chaton${stats.publicPlayers > 1 ? 's' : ''}`;
  }
}

function renderRoomList(host: HTMLElement, rooms: PublicRoomSummary[], router: Router): void {
  host.innerHTML = '';
  if (rooms.length === 0) {
    host.appendChild(
      el(
        'p',
        'lobby-empty',
        "Aucun salon public n'est ouvert pour le moment. Crée le premier !",
      ),
    );
    return;
  }
  for (const room of rooms) {
    const row = el('div', 'lobby-row');
    // Leading iconchat: differentiates open salons from "coins secrets"
    // (private rooms) at a glance. Open = crier (public, sociable);
    // private = refus (hides behind a closed door + needs a password).
    const kindIcon = document.createElement('img');
    kindIcon.className = 'lobby-row-kind-icon';
    kindIcon.src = room.isPrivate
      ? '/sprites/iconchat_refus.png'
      : '/sprites/iconchat_crier.png';
    kindIcon.alt = '';
    kindIcon.title = room.isPrivate
      ? 'Coin secret (mot de passe requis)'
      : 'Jardin ouvert (libre d\'accès)';
    row.appendChild(kindIcon);

    const left = el('div', 'lobby-row-left');
    const name = el('div', 'lobby-row-name', room.name);
    if (room.hasPassword) {
      const lock = el('span', 'lobby-row-lock', '🔒');
      lock.title = 'Mot de passe requis';
      name.appendChild(lock);
    }
    const meta = el(
      'div',
      'lobby-row-meta',
      `${room.players}/${room.maxPlayers} royaume${room.players > 1 ? 's' : ''}`,
    );
    left.append(name, meta);

    const join = makeButton(
      room.players >= room.maxPlayers ? 'Plein' : 'Rejoindre',
      'primary',
      () => void joinExisting(router, room),
    );
    if (room.players >= room.maxPlayers) {
      (join as HTMLButtonElement).disabled = true;
    }
    row.append(left, join);
    host.appendChild(row);
  }
}

// ----------------------------------------------------------------------------
// Create / join flows

async function promptCreate(router: Router, isPrivate: boolean): Promise<void> {
  const overlay = openModal();
  const card = el('div', 'modal-card');
  card.appendChild(
    el(
      'h2',
      'modal-title',
      isPrivate ? 'Cacher un coin secret' : 'Ouvrir un jardin public',
    ),
  );
  const form = document.createElement('form');
  form.className = 'modal-form';

  const name = textInput('Nom du salon', 'name', 'Mon royaume');
  form.appendChild(name.wrap);

  let pw: { wrap: HTMLElement; input: HTMLInputElement } | null = null;
  if (isPrivate) {
    pw = textInput('Mot de passe', 'password', '', 'password');
    form.appendChild(pw.wrap);
  }

  const error = el('div', 'auth-error');
  form.appendChild(error);

  const buttons = el('div', 'modal-buttons');
  const cancel = makeButton('Annuler', 'secondary', () => closeModal(overlay));
  const submit = makeButton(
    isPrivate ? 'Créer (privée)' : 'Créer',
    'primary',
    () => void 0,
  );
  (submit as HTMLButtonElement).type = 'submit';
  buttons.append(cancel, submit);
  form.appendChild(buttons);

  card.appendChild(form);
  overlay.appendChild(card);

  setTimeout(() => name.input.focus(), 0);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    const opts: Record<string, unknown> = {
      name: name.input.value.trim() || 'Salle sans nom',
      isPrivate,
    };
    if (isPrivate) {
      const pwVal = pw?.input.value.trim() ?? '';
      if (pwVal.length < 1) {
        error.textContent = 'Choisis un mot de passe.';
        return;
      }
      opts.password = pwVal;
    }
    (submit as HTMLButtonElement).disabled = true;
    submit.textContent = 'Création…';
    try {
      const room = await withTimeout(
        colyseusClient.create('lobby', opts),
        COLYSEUS_TIMEOUT_MS,
        'Le serveur multijoueur ne répond pas. Vérifie qu\'il tourne (npm run dev).',
      );
      // Stash the room handle — the room screen reads from this.
      (window as unknown as { __wolRoom?: typeof room }).__wolRoom = room;
      closeModal(overlay);
      router.navigate(`/room/${room.roomId}`);
    } catch (err) {
      error.textContent = (err as Error)?.message ?? 'Impossible de créer la salle.';
      (submit as HTMLButtonElement).disabled = false;
      submit.textContent = isPrivate ? 'Créer (privée)' : 'Créer';
    }
  });
}

async function joinExisting(router: Router, summary: PublicRoomSummary): Promise<void> {
  let password: string | undefined;
  if (summary.hasPassword) {
    const pw = window.prompt(`Mot de passe pour « ${summary.name} »`);
    if (pw === null) return; // user cancelled
    password = pw;
  }
  try {
    const room = await withTimeout(
      colyseusClient.joinById(summary.roomId, password ? { password } : {}),
      COLYSEUS_TIMEOUT_MS,
      'Le serveur multijoueur ne répond pas.',
    );
    (window as unknown as { __wolRoom?: typeof room }).__wolRoom = room;
    router.navigate(`/room/${room.roomId}`);
  } catch (err) {
    window.alert((err as Error)?.message ?? 'Impossible de rejoindre.');
  }
}

// ----------------------------------------------------------------------------
// Tiny modal + form helpers

function openModal(): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal(overlay);
  });
  document.body.appendChild(overlay);
  return overlay;
}

function closeModal(overlay: HTMLDivElement): void {
  overlay.remove();
}

function textInput(
  label: string,
  name: string,
  placeholder: string,
  type: 'text' | 'password' = 'text',
): { wrap: HTMLLabelElement; input: HTMLInputElement } {
  const wrap = el('label', 'auth-field') as HTMLLabelElement;
  wrap.appendChild(el('span', 'auth-field-label', label));
  const input = document.createElement('input');
  input.type = type;
  input.name = name;
  input.placeholder = placeholder;
  input.className = 'auth-input';
  if (type === 'password') input.autocomplete = 'new-password' as AutoFill;
  wrap.appendChild(input);
  return { wrap, input };
}

function makeButton(
  text: string,
  variant: 'primary' | 'secondary',
  onClick: () => void,
): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = variant === 'primary' ? 'btn-primary' : 'btn-secondary';
  b.textContent = text;
  b.addEventListener('click', onClick);
  return b;
}

/**
 * Compact profile card embedded at the top of the home hub. Shows the
 * user's name, ranked tier (with the metallic frame), point total
 * and "X games / Y wins" line. Clicking anywhere takes the user to
 * the full profile screen for the details + tabs.
 */
function renderProfileWidget(router: Router): HTMLElement {
  const card = el('div', 'home-profile-widget');
  const user = auth.user;
  if (!user) {
    card.appendChild(el('p', 'home-profile-empty', 'Pas connecté.'));
    return card;
  }
  // Make the whole card clickable — single visual target.
  card.addEventListener('click', () => router.navigate('/profile'));
  card.title = 'Aller à mon coussin';
  card.style.cursor = 'pointer';

  // Avatar (initial in a beige tile).
  const avatar = el('div', 'home-profile-avatar', user.username[0]?.toUpperCase() ?? '?');
  card.appendChild(avatar);

  // Middle column — name + rank line.
  const main = el('div', 'home-profile-main');
  const name = el('div', 'home-profile-name', user.username);
  main.appendChild(name);

  const r = rankFromPoints(user.rankedPoints);
  const rankRow = el('div', 'home-profile-rank-row');
  // Metallic-framed rank icon (same `.rank-frame` recipe as profile/leaderboard).
  const frame = el('div', 'home-profile-rank-frame rank-frame');
  frame.style.setProperty('--rank-tint', r.tier.tintColor);
  frame.style.setProperty('--rank-tint-deep', r.tier.tintColorDeep);
  const rankIcon = document.createElement('img');
  rankIcon.src = r.tier.icon;
  rankIcon.className = 'home-profile-rank-icon';
  rankIcon.alt = '';
  frame.appendChild(rankIcon);
  rankRow.appendChild(frame);

  const rankInfo = el('div', 'home-profile-rank-info');
  rankInfo.appendChild(el('div', 'home-profile-rank-name', r.tier.name));
  rankInfo.appendChild(
    el(
      'div',
      'home-profile-rank-pts',
      `${user.rankedPoints} pts · ${user.rankedGamesWon}/${user.rankedGamesPlayed} en classé`,
    ),
  );
  rankRow.appendChild(rankInfo);
  main.appendChild(rankRow);

  card.appendChild(main);

  // Right edge — chevron hinting that the card is a link.
  const chev = el('span', 'home-profile-chev', '›');
  card.appendChild(chev);

  return card;
}

/**
 * Three big action buttons: open a public room, hide a private room,
 * or queue for ranked. Wraps the existing prompts so we only need
 * to maintain one set of logic.
 *
 * The "ranked" button gets two live counter pills attached to its
 * right edge (currently playing / currently searching). The counters
 * are updated by `applyLiveStats` on every poll tick.
 */
function renderHomeActions(router: Router): HTMLElement {
  const wrap = el('div', 'home-actions');
  wrap.appendChild(actionButton(
    'iconchat_crier',
    'Ouvrir un jardin public',
    'Tout le monde peut rejoindre — meilleur pour les chats sociables',
    () => void promptCreate(router, false),
    'home-action-public',
  ));
  wrap.appendChild(actionButton(
    'iconchat_refus',
    'Cacher un coin secret',
    'Salon privé avec mot de passe — entre potes uniquement',
    () => void promptCreate(router, true),
    'home-action-private',
  ));
  // Ranked button — with live counters wedged into the body.
  const rankedBtn = actionButton(
    'iconchat_champion',
    'Partir à la chasse classée',
    'Matchmaking 2-8 joueurs · ton royaume favori auto-équipé',
    () => promptRankedQueue(router),
    'home-action-ranked',
  );
  rankedBtn.appendChild(renderRankedCounters());
  wrap.appendChild(rankedBtn);
  // Défi du jour — same map + same three hard bots for everyone today,
  // one scored attempt, daily board.
  wrap.appendChild(actionButton(
    'iconchat_guerrier',
    'Défi du jour',
    'Une seule chasse par jour · même carte pour tout le monde · 3 bots coriaces',
    () => void openDailyModal(router),
    'home-action-daily',
  ));
  // Tutorial — funny cat-themed onboarding. Launches a private game
  // with one bot, walks the player through every system.
  wrap.appendChild(actionButton(
    'iconchat_interrogation',
    'Apprendre à miauler (tuto)',
    'Petit chaton perdu ? Un guide chat vient ronronner les règles dans ton oreille.',
    () => void startTutorial(router),
    'home-action-tutorial',
  ));
  return wrap;
}

// ----------------------------------------------------------------------------
// Spectator list + daily challenge

function renderWatchList(host: HTMLElement, rooms: WatchableRoom[], router: Router): void {
  host.innerHTML = '';
  if (rooms.length === 0) {
    host.appendChild(el('p', 'lobby-empty', 'Aucune chasse en cours pour le moment — les chats dorment.'));
    return;
  }
  for (const room of rooms) {
    const row = el('div', 'lobby-row home-watch-row');
    const kindIcon = document.createElement('img');
    kindIcon.className = 'lobby-row-kind-icon';
    kindIcon.src = room.ranked
      ? '/sprites/iconchat_champion.png'
      : room.daily ? '/sprites/iconchat_guerrier.png' : '/sprites/iconchat_crier.png';
    kindIcon.alt = '';
    row.appendChild(kindIcon);
    const left = el('div', 'lobby-row-left');
    left.appendChild(el('div', 'lobby-row-name', room.name));
    const tags: string[] = [];
    tags.push(`${room.participantCount} royaume${room.participantCount > 1 ? 's' : ''}`);
    if (room.ranked) tags.push('classée');
    if (room.daily) tags.push('défi du jour');
    if (room.hasPassword) tags.push('mot de passe');
    left.appendChild(el('div', 'lobby-row-meta', tags.join(' · ')));
    const watch = makeButton('Regarder', 'secondary', () => void spectateRoom(router, room));
    row.append(left, watch);
    host.appendChild(row);
  }
}

async function spectateRoom(router: Router, summary: WatchableRoom): Promise<void> {
  ensureMusicPlaying();
  let password: string | undefined;
  if (summary.hasPassword) {
    const pw = window.prompt(`Mot de passe pour regarder « ${summary.name} »`);
    if (pw === null) return;
    password = pw;
  }
  try {
    const room = await withTimeout(
      colyseusClient.joinById(summary.roomId, { spectate: true, ...(password ? { password } : {}) }),
      COLYSEUS_TIMEOUT_MS,
      'Le serveur multijoueur ne répond pas.',
    );
    (window as unknown as { __wolRoom?: typeof room }).__wolRoom = room;
    router.navigate(`/room/${room.roomId}`);
  } catch (err) {
    flashBanner((err as Error)?.message ?? 'Impossible de regarder cette chasse.');
  }
}

/**
 * Daily challenge modal: today's board (top 15 + me) and the launch
 * button — disabled once the player already has a finished result for
 * today. The room is created like the tutorial (private, auto-started)
 * but with `daily: true` so the server pins the seed and the bot line-up.
 */
async function openDailyModal(router: Router): Promise<void> {
  if (!auth.user) { flashBanner('Connecte-toi d\'abord pour relever le défi.'); return; }
  document.querySelectorAll('.daily-modal-overlay').forEach((n) => n.remove());
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay daily-modal-overlay';
  const card = el('div', 'modal-card daily-modal-card');
  const title = document.createElement('h2');
  title.className = 'modal-title';
  const titleIcon = document.createElement('img');
  titleIcon.src = '/sprites/iconchat_guerrier.png';
  titleIcon.className = 'page-title-icon';
  titleIcon.alt = '';
  title.appendChild(titleIcon);
  title.appendChild(document.createTextNode('Défi du jour'));
  card.appendChild(title);
  const status = el('p', 'modal-help', 'Chargement du tableau…');
  card.appendChild(status);
  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);

  let board: DailyBoard;
  try {
    board = await fetchDailyBoard();
  } catch {
    status.textContent = 'Le tableau du jour est indisponible pour le moment.';
    return;
  }
  status.textContent = `${board.day} · même carte pour tous · toi contre ${board.botCount} bots coriaces · une seule tentative comptée.`;

  const rules = el('p', 'modal-help daily-rules',
    'Score : victoire = 1000 pts + bonus de rapidité + 5 pts par territoire · défaite = 5 pts par territoire tenu.');
  card.appendChild(rules);

  const table = el('div', 'daily-board');
  if (board.top.length === 0) {
    table.appendChild(el('p', 'lobby-empty', 'Personne n\'a encore relevé le défi aujourd\'hui. Sois le premier chat !'));
  } else {
    const rows = [...board.top];
    if (board.me && !rows.some((r) => r.isMe)) rows.push(board.me);
    for (const r of rows) {
      const row = el('div', 'daily-board-row' + (r.isMe ? ' daily-board-me' : ''));
      row.appendChild(el('span', 'daily-board-rank', `#${r.rank}`));
      if (r.factionId) {
        const head = document.createElement('img');
        head.src = `/sprites/${r.factionId}_army.png`;
        head.className = 'daily-board-head';
        head.alt = '';
        row.appendChild(head);
      }
      row.appendChild(el('span', 'daily-board-name', r.username));
      const outcome = document.createElement('img');
      outcome.src = r.won ? '/sprites/iconchat_champion.png' : '/sprites/iconchat_perdu.png';
      outcome.className = 'daily-board-outcome';
      outcome.alt = r.won ? 'Victoire' : 'Défaite';
      outcome.title = `${r.won ? 'Victoire' : 'Défaite'} en ${r.turns} patrouille${r.turns > 1 ? 's' : ''} · ${r.regions} territoire${r.regions > 1 ? 's' : ''}`;
      row.appendChild(outcome);
      row.appendChild(el('span', 'daily-board-score', `${r.score} pts`));
      table.appendChild(row);
    }
  }
  card.appendChild(table);

  const buttons = el('div', 'modal-buttons');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-secondary';
  cancel.textContent = 'Fermer';
  cancel.addEventListener('click', () => overlay.remove());
  const launch = document.createElement('button');
  launch.type = 'button';
  launch.className = 'btn-primary';
  if (board.played) {
    launch.textContent = 'Défi déjà relevé aujourd\'hui';
    launch.disabled = true;
  } else {
    launch.textContent = 'Relever le défi';
    launch.addEventListener('click', () => {
      launch.disabled = true;
      overlay.remove();
      void startDaily(router);
    });
  }
  buttons.append(cancel, launch);
  card.appendChild(buttons);
}

async function startDaily(router: Router): Promise<void> {
  ensureMusicPlaying();
  if (!auth.user) return;
  let factions: Awaited<ReturnType<typeof fetchFactions>>;
  try {
    factions = await fetchFactions();
  } catch {
    flashBanner('Impossible de charger les royaumes — réessaye.');
    return;
  }
  const favoriteId = auth.user.favoriteFactionId ?? '';
  const playerFaction =
    factions.find((f) => f.id === favoriteId)?.id
    ?? factions[Math.floor(Math.random() * factions.length)]!.id;
  let room: Awaited<ReturnType<typeof colyseusClient.create>>;
  try {
    room = await withTimeout(
      colyseusClient.create('lobby', {
        isPrivate: true,
        daily: true,
        dailyFactionId: playerFaction,
      }),
      COLYSEUS_TIMEOUT_MS,
      'Le serveur multijoueur ne répond pas.',
    );
  } catch (err) {
    flashBanner((err as Error)?.message ?? 'Impossible de lancer le défi.');
    return;
  }
  (window as unknown as { __wolRoom?: typeof room }).__wolRoom = room;
  try {
    room.send('faction.set', { factionId: playerFaction });
    room.send('ready.toggle');
    room.send('game.start');
  } catch (err) {
    flashBanner((err as Error)?.message ?? 'Le défi n\'a pas pu démarrer.');
    return;
  }
  router.navigate(`/room/${room.roomId}`);
}

/** Two pill counters next to the ranked button:
 *    🗡 playing  + 🐾 searching
 *  Values default to "—" and get filled in by `applyLiveStats`. */
function renderRankedCounters(): HTMLElement {
  const stats = el('div', 'home-ranked-stats');

  const playing = el('div', 'home-ranked-stat home-ranked-stat-playing');
  const playingIcon = document.createElement('img');
  playingIcon.src = '/sprites/iconchat_guerrier.png';
  playingIcon.className = 'home-ranked-stat-icon';
  playingIcon.alt = '';
  playing.appendChild(playingIcon);
  playing.appendChild(el('span', 'home-ranked-stat-playing-value', '—'));
  playing.title = 'Chatons actuellement en partie classée';
  stats.appendChild(playing);

  const searching = el('div', 'home-ranked-stat home-ranked-stat-searching');
  const searchingIcon = document.createElement('img');
  searchingIcon.src = '/sprites/iconchat_neutre.png';
  searchingIcon.className = 'home-ranked-stat-icon';
  searchingIcon.alt = '';
  searching.appendChild(searchingIcon);
  searching.appendChild(el('span', 'home-ranked-stat-searching-value', '—'));
  searching.title = 'Chatons actuellement en train de chercher';
  stats.appendChild(searching);

  return stats;
}

function actionButton(
  iconName: string,
  label: string,
  subtitle: string,
  onClick: () => void,
  variantClass: string,
): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `home-action ${variantClass}`;
  const ico = document.createElement('img');
  ico.src = `/sprites/${iconName}.png`;
  ico.className = 'home-action-icon';
  ico.alt = '';
  b.appendChild(ico);
  const text = el('span', 'home-action-text');
  text.appendChild(el('span', 'home-action-label', label));
  text.appendChild(el('span', 'home-action-sub', subtitle));
  b.appendChild(text);
  b.addEventListener('click', onClick);
  return b;
}

/**
 * Ranked queue entry — pops a small modal where the player picks the
 * desired match size (2..8 players). Pass 1 ships the modal + the
 * "no favorite faction" guard. The actual matchmaking pipeline that
 * wires this up to a Colyseus queue lands in Pass 2.
 */
function promptRankedQueue(router: Router): void {
  // Pre-flight: the user must have picked a favorite faction in their
  // profile, otherwise the queue has no kingdom to lock in for them.
  if (!auth.user?.favoriteFactionId) {
    flashNeedsFavorite();
    return;
  }
  document.querySelectorAll('.ranked-modal-overlay').forEach((n) => n.remove());
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay ranked-modal-overlay';
  const card = el('div', 'modal-card ranked-modal-card');

  const title = document.createElement('h2');
  title.className = 'modal-title';
  const titleIcon = document.createElement('img');
  titleIcon.src = '/sprites/iconchat_champion.png';
  titleIcon.className = 'page-title-icon';
  titleIcon.alt = '';
  title.appendChild(titleIcon);
  title.appendChild(document.createTextNode('Partir à la chasse classée'));
  card.appendChild(title);

  card.appendChild(
    el('p', 'modal-help', 'Choisis le MAXIMUM de chatons que tu acceptes sur la carte. Le matchmaker assemble la plus grosse partie possible jusqu\'à ta limite : ex. tu choisis 5 → tu peux tomber sur un duel, un trio, un quatuor ou un quintet selon qui attend en file.'),
  );

  // Size grid — 2 to 8 players. Default selection: 4.
  let selectedSize = 4;
  const grid = el('div', 'ranked-size-grid');
  const sizeBtns: HTMLButtonElement[] = [];
  for (let n = 2; n <= 8; n++) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ranked-size-btn' + (n === selectedSize ? ' is-active' : '');
    btn.textContent = `${n}`;
    btn.title = `Maximum ${n} chatons (tu accepteras des cohortes de 2 à ${n})`;
    btn.addEventListener('click', () => {
      selectedSize = n;
      for (const b of sizeBtns) b.classList.toggle('is-active', Number(b.textContent) === n);
    });
    sizeBtns.push(btn);
    grid.appendChild(btn);
  }
  card.appendChild(grid);
  card.appendChild(
    el('p', 'modal-help ranked-size-hint', 'Maximum acceptable · ta partie pourra avoir moins de chatons que ça'),
  );

  const buttons = el('div', 'modal-buttons');
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-secondary';
  cancel.textContent = 'Annuler';
  cancel.addEventListener('click', () => overlay.remove());
  const launch = document.createElement('button');
  launch.type = 'button';
  launch.className = 'btn-primary';
  launch.textContent = 'Lancer la recherche';
  launch.addEventListener('click', () => {
    overlay.remove();
    startRankedMatchmaking(router, selectedSize);
  });
  buttons.append(cancel, launch);
  card.appendChild(buttons);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

function flashNeedsFavorite(): void {
  flashBanner('Choisis d\'abord ton royaume favori dans ton profil avant de lancer une partie classée.');
}

let bannerTimer: number | null = null;
function flashBanner(message: string): void {
  document.querySelectorAll('.lobby-flash-banner').forEach((n) => n.remove());
  const b = document.createElement('div');
  b.className = 'lobby-flash-banner';
  b.textContent = message;
  document.body.appendChild(b);
  if (bannerTimer !== null) window.clearTimeout(bannerTimer);
  bannerTimer = window.setTimeout(() => { b.remove(); bannerTimer = null; }, 3200);
}
